import { DownloadOutlined } from "@ant-design/icons";
import { Alert, Button, DatePicker, Form, Input, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getSessions } from "../../api/logs.api";
import Card from "../../components/ui/Card";
import Spinner from "../../components/ui/Spinner";
import Table from "../../components/ui/Table";
import {
  formatDate,
  formatDuration,
  formatShortId,
} from "../../utils/formatters";

interface SessionRecord {
  id: string;
  user_email: string | null;
  started_at: string;
  ended_at: string | null;
  total_messages: number;
  metadata?: {
    last_intent?: string;
    last_detected_language?: string;
    last_model_name?: string;
    location?: { city?: string; country?: string };
  };
}

const cleanIntent = (value?: string | null): string => {
  if (!value) return "—";
  const parts = value.split(".");
  return parts[parts.length - 1];
};

const buildCsv = (sessions: SessionRecord[]) => {
  const headers = [
    "Session ID",
    "Started",
    "Ended",
    "Duration",
    "Messages",
    "Intent",
    "Language",
    "Model",
  ];
  const rows = sessions.map((s: any) => [
    s.id,
    s.started_at,
    s.ended_at ?? "",
    s.ended_at ? formatDuration(s.started_at, s.ended_at) : "Active",
    s.total_messages,
    cleanIntent(s.metadata?.last_intent),
    s.metadata?.last_detected_language ?? "",
    s.metadata?.last_model_name ?? "",
  ]);
  return [headers, ...rows]
    .map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")
    )
    .join("\n");
};

const triggerDownload = (csv: string) => {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sessions-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const SessionsPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({ start_date: "", end_date: "" });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: response } = await getSessions({
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        page,
        per_page: pageSize,
      });
      setData(response);
    } catch (_error) {
      setError("Unable to load sessions right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [filters, page, pageSize]);

  const handleExport = async () => {
    try {
      const all: SessionRecord[] = [];
      let p = 1;
      let hasMore = true;
      while (hasMore) {
        const { data: response } = await getSessions({
          start_date: filters.start_date || undefined,
          end_date: filters.end_date || undefined,
          page: p,
          per_page: 100,
        });
        all.push(...response.sessions);
        hasMore =
          all.length < response.total && response.sessions.length === 100;
        p++;
      }
      triggerDownload(buildCsv(all));
    } catch {
      // ignore export errors silently
    }
  };

  if (loading && !data) {
    return <Spinner />;
  }

  const allSessions = data?.sessions ?? [];
  const sessions = search
    ? allSessions.filter(
        (s: SessionRecord) =>
          s.id.toLowerCase().includes(search.toLowerCase()) ||
          cleanIntent(s.metadata?.last_intent)
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          (s.metadata?.last_detected_language ?? "")
            .toLowerCase()
            .includes(search.toLowerCase())
      )
    : allSessions;

  return (
    <div className="page-stack">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography.Title level={2} style={{ margin: 0 }}>
          Sessions
        </Typography.Title>
        <Button
          icon={<DownloadOutlined />}
          disabled={!data?.total}
          onClick={handleExport}
        >
          Export CSV
        </Button>
      </div>

      <Card>
        <Form layout="inline">
          <Form.Item label="From">
            <DatePicker
              onChange={value => {
                setPage(1);
                setFilters(cur => ({
                  ...cur,
                  start_date: value ? value.startOf("day").toISOString() : "",
                }));
              }}
            />
          </Form.Item>
          <Form.Item label="To">
            <DatePicker
              onChange={value => {
                setPage(1);
                setFilters(cur => ({
                  ...cur,
                  end_date: value ? value.endOf("day").toISOString() : "",
                }));
              }}
            />
          </Form.Item>
          <Form.Item>
            <Input.Search
              placeholder="Search session, intent, language..."
              allowClear
              style={{ width: 280 }}
              onChange={e => setSearch(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Card>

      {error ? <Alert type="error" showIcon message={error} /> : null}

      <Card>
        <Table
          dataSource={sessions}
          pagination={{
            total: data?.total ?? 0,
            current: page,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100"],
            onChange: (newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            },
          }}
          onRow={(record: SessionRecord) => ({
            onClick: () => navigate(`/sessions/${record.id}`),
          })}
          columns={[
            { title: "Session", dataIndex: "id", render: formatShortId },
            {
              title: "Started",
              dataIndex: "started_at",
              render: (value: string) => formatDate(value),
            },
            {
              title: "Ended",
              dataIndex: "ended_at",
              render: (value: string) => (value ? formatDate(value) : "Active"),
            },
            {
              title: "Duration",
              render: (_: unknown, record: SessionRecord) =>
                record.ended_at
                  ? formatDuration(record.started_at, record.ended_at)
                  : "Active",
            },
            { title: "Messages", dataIndex: "total_messages" },
            {
              title: "Intent",
              render: (_: unknown, record: SessionRecord) =>
                cleanIntent(record.metadata?.last_intent),
            },
            {
              title: "Language",
              render: (_: unknown, record: SessionRecord) =>
                record.metadata?.last_detected_language ?? "—",
            },
            {
              title: "Model",
              render: (_: unknown, record: SessionRecord) =>
                record.metadata?.last_model_name ?? "—",
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default SessionsPage;
